<template>
  <div class="InsightMatrixPreview">
    <lm-logo v-show="resizing" />
    <svg id="IMatrix" :width="width" :height="height" />
  </div>
</template>

<script>
import LmLogo from '@/components/ui/LmLogo'
import {mapGetters} from 'vuex'
import {select} from 'd3-selection'
import {scaleLinear} from 'd3-scale'
import {axisBottom, axisLeft} from 'd3-axis'

const d3 = Object.assign(
  {},
  {
    select,
    scaleLinear,
    axisBottom,
    axisLeft
  }
)

export default {
  name: 'InsightMatrixPreview',
  components: {LmLogo},
  props: {},
  data() {
    return {
      width: 0,
      height: 0,
      scaled: {
        x: d3.scaleLinear().domain([-10, 110]),
        y: d3.scaleLinear().domain([-10, 110])
      },
      resizing: false,
      timeout: null
    }
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters({
      matrixLabels: 'insights/settings/matrixLabels'
    })
  },
  watch: {
    $route() {
      this.update()
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    this.$nextTick(() => {
      this.update()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      if (!this.resizing) {
        this.resizing = true
        this.width = 0
        this.height = 0
        this.cleanSVG()
      }
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.resizing = false
        this.update()
      }, 400)
    },
    update() {
      // Refresh values
      this.width = this.$el.offsetWidth
      this.height = this.$el.offsetHeight
      if (this.isMobile) {
        const max = 400
        this.height = this.width > max ? max : this.width
      }
      this.scaled.x.range([0, this.width])
      d3.axisLeft().scale(this.scaled.x)
      this.scaled.y.range([this.height, 0])
      d3.axisBottom().scale(this.scaled.y)
      // Draw
      this.cleanSVG()
      this.drawAxis()
      this.drawLabels()
    },
    cleanSVG() {
      d3.select('.axis').remove()
      d3.select('.labels').remove()
    },
    drawAxis() {
      const axis = d3
        .select('#IMatrix')
        .append('g')
        .attr('class', 'axis')
      axis
        .append('g')
        .attr('transform', `translate(0, ${this.scaled.y.range()[0] / 2})`)
        .call(d3.axisBottom(this.scaled.x).ticks(0))
      axis
        .append('g')
        .attr('transform', `translate(${this.scaled.x.range()[1] / 2}, 0)`)
        .call(d3.axisLeft(this.scaled.y).ticks(0))
    },
    drawLabels() {
      const posY = this.scaled.y.range()[0]
      const posX = this.scaled.x.range()[1]
      const labels = d3
        .select('#IMatrix')
        .append('g')
        .attr('class', 'labels')
      labels
        .append('text')
        .attr('x', 0)
        .attr('y', posY / 2 + 25)
        .classed('uppercase', true)
        .text(this.matrixLabels.Axis1.min)
      labels
        .append('text')
        .attr('style', 'direction:rtl;')
        .attr('x', posX - 10)
        .attr('y', posY / 2 + 25)
        .classed('uppercase', true)
        .text(this.matrixLabels.Axis1.max)
      labels
        .append('text')
        .attr('x', posX / 2 + 15)
        .attr('y', 10)
        .classed('uppercase', true)
        .text(this.matrixLabels.Axis2.max)
      labels
        .append('text')
        .attr('x', posX / 2 + 15)
        .attr('y', posY - 5)
        .classed('uppercase', true)
        .text(this.matrixLabels.Axis2.min)
      labels
        .append('text')
        .attr('x', 0)
        .attr('y', posY - 5)
        .text(`Circle size reflects ${this.matrixLabels.Axis3.legend}`)
    }
  }
}
</script>

<style>
.InsightMatrixPreview {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  .axis path {
    stroke: theme('colors.gray.500');
  }
  .labels text {
    fill: theme('colors.gray.700');
    font-size: 11px;
  }
  .node {
    cursor: pointer;
    .number {
      font-family: theme('fontFamily.mono');
      font-size: 16px;
      fill: white;
    }
  }
}
</style>
